<template>
  <div class="cool-stuff-overlay">
    <!-- Stroke-only text - always visible at highest z-index -->
    <div class="fixed pointer-events-none w-full h-full z-[9999] lowercase">
      <svg :key="`stroke-${displayText}`" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 300 60" class="fixed pointer-events-none z-[9999] animText">
        <defs>
          <clipPath id="crack1-stroke">
            <polygon points="-30,0 30,0 180,60 -30,60" />
          </clipPath>
          <clipPath id="crack2-stroke">
            <polygon points="30,0 120,0 240,60 180,60" />
          </clipPath>
          <clipPath id="crack3-stroke">
            <polygon points="120,0 330,0 330,60 240,60" />
          </clipPath>
        </defs>
        
        <!-- Left crack piece -->
        <text textLength="290" lengthAdjust="spacing" font-size="60" fill="none" stroke-width=".02" 
              :stroke="strokeColor" font-family="serif" x="5" y="40" 
              clip-path="url(#crack1-stroke)">
          {{ displayText }}
        </text>
        
        <!-- Middle piece -->
        <text textLength="290" lengthAdjust="spacing" font-size="60" fill="none" stroke-width=".02" 
              :stroke="strokeColor" font-family="serif" x="5" y="40" 
              clip-path="url(#crack2-stroke)">
          {{ displayText }}
        </text>
        
        <!-- Right crack piece -->
        <text textLength="290" lengthAdjust="spacing" font-size="60" fill="none" stroke-width=".02" 
              :stroke="strokeColor" font-family="serif" x="5" y="40" 
              clip-path="url(#crack3-stroke)">
          {{ displayText }}
        </text>
      </svg>
    </div>
    <!-- Filled text - positioned behind content but above backgrounds -->
    <div class="w-full h-auto -z-[1] lowercase">
      <svg :key="`fill-${displayText}`" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 300 60" class="fixed pointer-events-none animText z-[1]">
        <defs>
          <clipPath id="crack1-fill">
            <polygon points="-30,0 30,0 180,60 -30,60" />
          </clipPath>
          <clipPath id="crack2-fill">
            <polygon points="30,0 120,0 240,60 180,60" />
          </clipPath>
          <clipPath id="crack3-fill">
            <polygon points="120,0 330,0 330,60 240,60" />
          </clipPath>
        </defs>
        
        <!-- Left crack piece -->
        <text textLength="290" lengthAdjust="spacing" font-size="60" stroke-width=".3" 
font-family="serif" x="5" y="40" :fill="fillColor" class="crack-piece-1" 
              clip-path="url(#crack1-fill)">
          {{ displayText }}
        </text>
        
        <!-- Middle piece -->
        <text textLength="290" lengthAdjust="spacing" font-size="60" stroke-width=".3" 
              font-family="serif" x="5" y="40" :fill="fillColor" 
              clip-path="url(#crack2-fill)">
          {{ displayText }}
        </text>
        
        <!-- Right crack piece -->
        <text textLength="290" lengthAdjust="spacing" font-size="60" stroke-width=".3" 
              font-family="serif" x="5" y="40" :fill="fillColor" class="crack-piece-3" 
              clip-path="url(#crack3-fill)">
          {{ displayText }}
        </text>
      </svg>
    </div>
  </div>
</template>

<script setup>
import { watch, computed } from 'vue'
import { useRoute, useI18n } from '#imports'
import { useCoolStuff } from '~/composables/useCoolStuff'
import { useContentStudio } from '~/composables/useContentStudio'
import { useGlobalTheme } from '~/composables/useGlobalTheme'
import { useArtists } from '~/composables/useArtists'

// Accept backgroundColor prop (default to white)
const props = defineProps({
  backgroundColor: {
    type: String,
    default: '#ffffff'
  }
})

const route = useRoute()
const { t } = useI18n()
const { currentText } = useCoolStuff()
const { contentData, loadContentData } = useContentStudio()
const { getArtistBySlug } = useArtists()

// Load Content Studio data immediately (before computed properties are evaluated)
loadContentData()

const { getEffectiveTheme, getSampleProductPageBackgroundColor } = useGlobalTheme()

// Use brand text on sample product page, otherwise use currentText
// currentText already handles artist pages and other routes
const displayText = computed(() => {
  if (route.path === '/sample-product-page') {
    return contentData.brand || t('pages.sampleProduct.defaults.brand')
  }
  return currentText.value
})

// Get effective background color based on theme
const effectiveBackgroundColor = computed(() => {
  const effectiveTheme = getEffectiveTheme()
  
  // If on sample product page, use Content Studio background
  if (effectiveTheme === 'content-studio') {
    // Use saved Content Studio background color if available, otherwise use current contentData
    return getSampleProductPageBackgroundColor() || contentData.backgroundColor || '#ffffff'
  }
  
  // If on artist page, use artist's background color
  if (route.path.startsWith('/of/')) {
    const pathParts = route.path.split('/')
    const slug = pathParts[pathParts.length - 1]
    if (slug) {
      const artist = getArtistBySlug(slug)
      if (artist?.backgroundColor) {
        return artist.backgroundColor
      }
    }
  }
  
  // Use the effective theme (system or global)
  if (effectiveTheme === 'night') {
    return '#000000'
  }
  
  return '#ffffff'
})

// Calculate if background is light or dark
const isLightBackground = computed(() => {
  const bgColor = effectiveBackgroundColor.value
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance >= 0.5
})

// Compute fill color - use the actual background color for fill
const fillColor = computed(() => {
  const bgColor = effectiveBackgroundColor.value
  return bgColor == '#ffffff' ? '#000000' : bgColor
})

// Compute stroke color based on background brightness (inverse for contrast)
const strokeColor = computed(() => {
  return isLightBackground.value ? '#000000' : '#ffffff'
})

// Ensure the component reacts to text changes
watch(displayText, (newText) => {
  // Force re-render by accessing the value
  // console.log('CoolStuff text changed to:', newText)
}, { immediate: true })

// Debug: Watch backgroundColor changes
watch(() => props.backgroundColor, (newBg) => {
  // console.log('CoolStuff backgroundColor changed to:', newBg)
  // console.log('Is light background:', isLightBackground.value)
  // console.log('Fill color:', fillColor.value)
  // console.log('Stroke color:', strokeColor.value)
}, { immediate: true })
</script>

<style scoped>
.cool-stuff-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.crack-piece-1 {
  animation: crack1 0.2s linear forwards;
}

.crack-piece-3 {
  animation: crack2 0.2s linear forwards;
}

@keyframes crack1 {
  0% {
    transform: translate(0%, 0%);
  }
  100% {
    transform: translate(-2%, 1%) rotate(1deg);
  }
}

@keyframes crack2 {
  0% {
    transform: translate(0%, 0%);
  }
  100% {
    transform: translate(2%, -1%) rotate(1deg);
  }
}
</style>
